
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Search, Filter } from "lucide-react";

export type SortOption = "alphabetical-asc" | "alphabetical-desc" | "date-asc" | "date-desc" | "usage-desc" | "usage-asc";
export type FilterOption = "all" | "quick" | "imported" | "never-used" | "recently-used" | "not-used-recently";
export type GroupOption = "all" | "never-used" | "recently-used";

interface RecipeFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  filterBy: FilterOption;
  onFilterChange: (filter: FilterOption) => void;
  groupBy: GroupOption;
  onGroupChange: (group: GroupOption) => void;
  excludeCompositions: boolean;
  onExcludeCompositionsChange: (exclude: boolean) => void;
  selectedTags?: string[];
  onTagRemove?: (tag: string) => void;
  onClearFilters: () => void;
  hideMobileFilters?: boolean;
}

const RecipeFilters: React.FC<RecipeFiltersProps> = ({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  filterBy,
  onFilterChange,
  groupBy,
  onGroupChange,
  excludeCompositions,
  onExcludeCompositionsChange,
  selectedTags = [],
  onTagRemove,
  onClearFilters,
  hideMobileFilters = false,
}) => {
  const hasActiveFilters = searchQuery || sortBy !== "date-desc" || groupBy !== "all" || excludeCompositions || selectedTags.length > 0;

  return (
    <div className="space-y-4 mb-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Search recipes and compositions..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Recipe grouping */}
      {!hideMobileFilters && (
        <Tabs value={groupBy} onValueChange={(value) => onGroupChange(value as GroupOption)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="never-used">Never Used</TabsTrigger>
            <TabsTrigger value="recently-used">Recent</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      {hideMobileFilters && (
        <div className="hidden md:block">
          <Tabs value={groupBy} onValueChange={(value) => onGroupChange(value as GroupOption)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="never-used">Never Used</TabsTrigger>
              <TabsTrigger value="recently-used">Recent</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Active Tag Filters:</h4>
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tag, index) => (
              <Badge 
                key={index}
                variant="secondary" 
                className="text-xs bg-primary/10 text-primary border-primary/20 cursor-pointer hover:bg-primary/20"
                onClick={() => onTagRemove?.(tag)}
              >
                {tag} ×
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Sort Control and Exclude Compositions Checkbox */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <Select value={sortBy} onValueChange={(value) => onSortChange(value as SortOption)}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Sort by..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date-desc">Newest First</SelectItem>
            <SelectItem value="date-asc">Oldest First</SelectItem>
            <SelectItem value="alphabetical-asc">A to Z</SelectItem>
            <SelectItem value="alphabetical-desc">Z to A</SelectItem>
            <SelectItem value="usage-desc">Most Used</SelectItem>
            <SelectItem value="usage-asc">Least Used</SelectItem>
          </SelectContent>
        </Select>

        {!hideMobileFilters && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="exclude-compositions"
              checked={excludeCompositions}
              onCheckedChange={onExcludeCompositionsChange}
            />
            <label
              htmlFor="exclude-compositions"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Exclude compositions
            </label>
          </div>
        )}
        {hideMobileFilters && (
          <div className="hidden md:flex items-center space-x-2">
            <Checkbox
              id="exclude-compositions"
              checked={excludeCompositions}
              onCheckedChange={onExcludeCompositionsChange}
            />
            <label
              htmlFor="exclude-compositions"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Exclude compositions
            </label>
          </div>
        )}

        {hasActiveFilters && (
          <Button variant="outline" onClick={onClearFilters} className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Clear Filters
          </Button>
        )}
      </div>
    </div>
  );
};

export default RecipeFilters;
